// components/common/InputWithAttachments.tsx
import React, { useRef, useState, ChangeEvent, useEffect } from "react";
import { Controller, Control, useFormContext } from "react-hook-form";
import { Plus } from "lucide-react";

interface Attachment {
    file_path: string;
    file_name: string;
    file_type: string
}

interface InputWithAttachmentsProps {
    name: string; // text input field name
    label?: string;
    control: Control<any>;
    uploadHandler: (file: File) => Promise<string>;
    acceptedTypes?: string;
    attachmentsName?: string; // name in form for attachments (default: `${name}Attachments`)
    placeholder: string;
}

export const InputWithAttachments: React.FC<InputWithAttachmentsProps> = ({
    name,
    label,
    control,
    uploadHandler,
    acceptedTypes = ".jpg,.jpeg,.png,.pdf,.doc,.docx",
    attachmentsName,
    placeholder
}) => {
    const { setValue } = useFormContext();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [attachments, setAttachments] = useState<Attachment[]>([]);
    const [isUploading, setIsUploading] = useState(false);
    const [validationMessage, setValidationMessage] = useState<string | null>(null);

    const fieldName = attachmentsName || `${name}Attachments`;

    // keep attachments synced to the form state
    useEffect(() => {
        setValue(fieldName, attachments);
    }, [attachments, fieldName, setValue]);

    const handleIconClick = () => {
        fileInputRef.current?.click();
    };

    const handleFileUpload = async (e: ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (!files || files.length === 0) return;

        setIsUploading(true);
        setValidationMessage(null);

        const validTypes = [
            "image/jpeg",
            "image/png",
            "application/pdf",
            "application/msword",
            "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        ];

        const newFiles: Attachment[] = [];

        for (const file of Array.from(files)) {
            if (!validTypes.includes(file.type)) {
                setValidationMessage("Only JPG, PNG, PDF, and DOC files are allowed.");
                setIsUploading(false);
                return;
            }

            try {
                const uploadedUrl = await uploadHandler(file);
                newFiles.push({ file_path: uploadedUrl, file_name: file.name, file_type: file.type });
            } catch (err) {
                console.error("Upload failed:", err);
            }
        }

        setAttachments((prev) => [...prev, ...newFiles]);
        setIsUploading(false);
    };

    const handleRemove = (index: number) => {
        setAttachments((prev) => prev.filter((_, i) => i !== index));
    };

    return (
        <div className="flex flex-col gap-2">
            {label && <label className=" text-gray-800 dark:text-gray-100 font-semibold">{label}</label>}

            {/* Input with + icon */}
            <div className="relative flex items-center border rounded-xl p-2 bg-white shadow-sm gap-[8px] idea-main-input max-h-[50px] py-[10px]">
                 <button
                    type="button"
                    onClick={handleIconClick}
                    className="ml-2 flex items-center justify-center w-8 h-8 rounded-full  plus-ideabtn-dv"
                >
                    <Plus className="w-5 h-5 text-white" />
                </button>
                <Controller
                    name={name}
                    control={control}
                    render={({ field }) => (
                        <textarea
                            {...field}
                            placeholder={placeholder}
                            className="flex-1 outline-none flex items-center border-none bg-transparent text-gray-800 text-sm relative top-[6px]"
                        />
                    )}
                />
               

                <input
                    type="file"
                    ref={fileInputRef}
                    style={{ display: "none" }}
                    onChange={handleFileUpload}
                    accept={acceptedTypes}
                    multiple
                />
            </div>

            {/* Uploading indicator */}
            {isUploading && <p className="text-blue-500 text-sm">Uploading...</p>}

            {/* File list */}
            {attachments.length > 0 && (
                <ul className="flex flex-col gap-1 mt-2 bg-white p-2 rounded-md border upload-idea-inputbox "  data-aos="fade-down">
                    {attachments.map((file, index) => (
                        <li
                            key={index}
                            className="flex justify-between items-center text-sm text-gray-700 bg-gray-50 px-3 py-2 rounded border relative"
                        >
                            <a
                                href={file.file_path}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="truncate hover:underline text-blue-600"
                            >
                                {file.file_name}
                            </a>
                            <button
                                type="button"
                                onClick={() => handleRemove(index)}
                                className="text-red-500 hover:text-red-700 text-xs font-semibold absolute right-0 top-[-10px] closeupload-btn"
                            >
                              <span className="text-white">  ✕</span>
                            </button>
                        </li>
                    ))}
                </ul>
            )}

            {/* Validation */}
            {validationMessage && (
                <p className="text-red-500 text-sm flex items-center gap-1">⚠️ {validationMessage}</p>
            )}
        </div>
    );
};
